import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    Divider,
    Chip,
    Avatar,
    Paper,
    IconButton,
    Tooltip,
    Badge,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    styled,
    Button,
    Snackbar
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { format, isSameDay, isToday, isTomorrow, isYesterday, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { BirebirOgretmenError } from '../../../types/ErrorModals/BirebirOgretmenError';
import { getBirebirOgretmen, approveLesson, withdrawApproval } from '../../../services/api/birebirOgretmenService';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SubjectIcon from '@mui/icons-material/Subject';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';

// Styled components
const SeasonSelector = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const Alert2 = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Helper function to group lessons by date
const groupByDate = (lessons: any[]) => {
    return lessons.reduce((acc, lesson) => {
        const dateKey = format(new Date(lesson.derstar), 'yyyy-MM-dd');
        if (!acc[dateKey]) {
            acc[dateKey] = [];
        }
        acc[dateKey].push(lesson);
        return acc;
    }, {});
};

// Helper function to get formatted date label
const getDateLabel = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Bugün';
    if (isTomorrow(date)) return 'Yarın';
    if (isYesterday(date)) return 'Dün';
    return format(date, 'dd MMMM yyyy', { locale: tr });
};

// Helper to get subject color
const getSubjectColor = (subject: string) => {
    const subjects: Record<string, string> = {
        'MATEMATİK': '#4caf50',
        'FİZİK': '#2196f3',
        'KİMYA': '#f44336',
        'BİYOLOJİ': '#ff9800',
        'TARİH': '#9c27b0',
        'COĞRAFYA': '#795548',
        'EDEBİYAT': '#607d8b',
        'İNGİLİZCE': '#00bcd4',
    };

    // Find a match (case insensitive)
    const match = Object.keys(subjects).find(key =>
        subject?.toUpperCase().includes(key.toUpperCase())
    );

    return match ? subjects[match] : '#757575'; // Default gray
};

// Helper to get initials from name
const getInitials = (name: string | undefined) => {
    if (!name) return '?';
    return name
        .split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const BirebirOgretmen: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { kullaniciModal } = useAuth();

    // State for view type (day, week, month)
    const [viewType, setViewType] = useState<number>(0);

    // State for season selection
    const [selectedSeason, setSelectedSeason] = useState<string>('2025');
    const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const queryClient = useQueryClient();

    // Handle tab change
    const handleViewChange = (event: React.SyntheticEvent, newValue: number) => {
        setViewType(newValue);
    };

    // Handle season change
    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['birebirOgretmen', kullaniciModal?.id, selectedSeason],
        queryFn: async () => {
            try {
                return await getBirebirOgretmen({
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    ogretmenId: parseInt(kullaniciModal?.id || '0'),
                    sezon: parseInt(selectedSeason) + 1
                });
            } catch (err) {
                const birebirError = err as BirebirOgretmenError;
                throw new Error(birebirError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1
    });

    // Add mutation hooks
    const approveMutation = useMutation({
        mutationFn: approveLesson,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['birebirOgretmen', kullaniciModal?.id, selectedSeason]
            });
            setSnackbarMessage('Ders başarıyla onaylandı');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        },
        onError: (error: any) => {
            setSnackbarMessage(error.message || 'Ders onaylanırken bir hata oluştu');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    });

    const withdrawMutation = useMutation({
        mutationFn: withdrawApproval,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['birebirOgretmen', kullaniciModal?.id, selectedSeason]
            });
            setSnackbarMessage('Ders onayı başarıyla geri çekildi');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        },
        onError: (error: any) => {
            setSnackbarMessage(error.message || 'Ders onayı geri çekilirken bir hata oluştu');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    });

    // Add handler functions
    const handleApproveLesson = (recNo: number) => {
        approveMutation.mutate({
            kurumKodu: localStorage.getItem('kurumKodu') || '',
            recNo,
            ogretmenId: parseInt(kullaniciModal?.id || '0'),
            sezon: parseInt(selectedSeason) + 1
        });
    };

    const handleWithdrawApproval = (recNo: number) => {
        withdrawMutation.mutate({
            kurumKodu: localStorage.getItem('kurumKodu') || '',
            recNo,
            ogretmenId: parseInt(kullaniciModal?.id || '0'),
            sezon: parseInt(selectedSeason) + 1
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    // Function to handle manual refresh
    const handleRefresh = () => {
        refetch();
    };

    // Group lessons by date
    const groupedLessons = useMemo(() => {
        if (!data) return {};
        return groupByDate(data);
    }, [data]);

    // Filter lessons based on view type
    const filteredDates = useMemo(() => {
        if (!groupedLessons) return [];

        const today = new Date();
        const dates = Object.keys(groupedLessons).sort().reverse();

        if (viewType === 0) { // Today
            return dates.filter(date => isToday(parseISO(date)));
        } else if (viewType === 1) { // This week
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            const endOfWeek = new Date(today);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            return dates.filter(date => {
                const dateObj = parseISO(date);
                return dateObj >= startOfWeek && dateObj <= endOfWeek;
            });
        } else { // All
            return dates;
        }
    }, [groupedLessons, viewType]);

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header with title, refresh button, and season selector */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
            }}>
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main
                    }}
                >
                    Birebir Derslerim
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SeasonSelector
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        <InputLabel id="teacher-season-select-label">Eğitim Sezonu</InputLabel>
                        <Select
                            labelId="teacher-season-select-label"
                            id="teacher-season-select"
                            value={selectedSeason}
                            onChange={handleSeasonChange}
                            label="Eğitim Sezonu"
                        >
                            {availableSeasons.map((season) => (
                                <MenuItem key={season} value={season}>
                                    {season} - {(parseInt(season) + 1)} Eğitim Yılı
                                </MenuItem>
                            ))}
                        </Select>
                    </SeasonSelector>

                    <Tooltip title="Yenile">
                        <IconButton
                            onClick={handleRefresh}
                            color="primary"
                            sx={{
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* View type tabs */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    mb: 3,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    overflow: 'hidden'
                }}
            >
                <Tabs
                    value={viewType}
                    onChange={handleViewChange}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: '48px',
                            fontWeight: 500
                        },
                        '& .Mui-selected': {
                            color: theme.palette.primary.main,
                            fontWeight: 600
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderTopLeftRadius: 3,
                            borderTopRightRadius: 3
                        }
                    }}
                >
                    <Tab
                        icon={<CalendarTodayIcon />}
                        label={isMobile ? undefined : "Bugün"}
                        iconPosition="start"
                    />
                    <Tab
                        icon={<CalendarViewWeekIcon />}
                        label={isMobile ? undefined : "Bu Hafta"}
                        iconPosition="start"
                    />
                    <Tab
                        icon={<CalendarMonthIcon />}
                        label={isMobile ? undefined : "Tümü"}
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    sx={{ mt: 2, borderRadius: 2 }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as BirebirOgretmenError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    {selectedSeason} - {parseInt(selectedSeason) + 1} eğitim sezonunda kayıtlı birebir ders bulunmamaktadır.
                </Alert>
            ) : filteredDates.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    {viewType === 0 ? 'Bugün için' : viewType === 1 ? 'Bu hafta için' : ''} kayıtlı birebir ders bulunmamaktadır.
                </Alert>
            ) : (
                <Box>
                    {filteredDates.map(dateKey => (
                        <Box key={dateKey} sx={{ mb: 4 }}>
                            {/* Date header */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                    pl: 1
                                }}
                            >
                                <EventIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 500,
                                        color: isToday(parseISO(dateKey))
                                            ? theme.palette.primary.main
                                            : theme.palette.text.primary
                                    }}
                                >
                                    {getDateLabel(dateKey)}
                                </Typography>
                                {isToday(parseISO(dateKey)) && (
                                    <Chip
                                        size="small"
                                        label="Bugün"
                                        color="primary"
                                        sx={{ ml: 1, height: 24 }}
                                    />
                                )}
                            </Box>

                            {/* Lesson cards */}
                            <Grid container spacing={2}>
                                {groupedLessons[dateKey].map((lesson: any, index: number) => {
                                    const subjectColor = getSubjectColor(lesson.ders);

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Card
                                                elevation={3}
                                                sx={{
                                                    borderRadius: 2,
                                                    height: '100%',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: 6
                                                    },
                                                    position: 'relative',
                                                    overflow: 'visible',
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '4px',
                                                        backgroundColor: subjectColor,
                                                        borderTopLeftRadius: '8px',
                                                        borderTopRightRadius: '8px'
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    {/* Lesson header */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                        <Chip
                                                            label={lesson.ders.trim()}
                                                            sx={{
                                                                backgroundColor: alpha(subjectColor, 0.1),
                                                                color: subjectColor,
                                                                fontWeight: 600,
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: theme.palette.text.secondary,
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                            {lesson.saat1} - {lesson.saat2}
                                                        </Typography>
                                                    </Box>

                                                    {/* Student info */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: alpha(subjectColor, 0.8),
                                                                color: '#fff',
                                                                width: 40,
                                                                height: 40,
                                                                mr: 2
                                                            }}
                                                        >
                                                            {getInitials(lesson.ogrenciadi?.toString())}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography
                                                                variant="subtitle1"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    lineHeight: 1.2
                                                                }}
                                                            >
                                                                {lesson.ogrenciadi}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="textSecondary"
                                                                sx={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <PersonIcon fontSize="small" sx={{ mr: 0.5, fontSize: 16 }} />
                                                                {lesson.ogretmen}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Divider sx={{ my: 1.5 }} />

                                                    {/* Location */}
                                                    {lesson.mekan && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                mb: 1
                                                            }}
                                                        >
                                                            <LocationOnIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    mr: 1,
                                                                    color: theme.palette.info.main
                                                                }}
                                                            />
                                                            {lesson.mekan}
                                                        </Typography>
                                                    )}

                                                    {/* Topic */}
                                                    {lesson.konu && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                mb: 1
                                                            }}
                                                        >
                                                            <SubjectIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    mr: 1,
                                                                    mt: 0.25,
                                                                    color: theme.palette.warning.main
                                                                }}
                                                            />
                                                            {lesson.konu}
                                                        </Typography>
                                                    )}

                                                    {/* Result */}
                                                    {lesson.sonuc && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: lesson.sonuc === '1'
                                                                    ? theme.palette.success.main
                                                                    : theme.palette.text.secondary,
                                                                mb: 2
                                                            }}
                                                        >
                                                            <CheckCircleIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    mr: 1,
                                                                    color: lesson.sonuc === '1'
                                                                        ? theme.palette.success.main
                                                                        : theme.palette.text.secondary
                                                                }}
                                                            />
                                                            {lesson.sonuc === '1' ? 'Tamamlandı' : 'Beklemede'}
                                                        </Typography>
                                                    )}

                                                    {/* Approval status */}
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            Onay Durumu:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                            <Chip
                                                                size="small"
                                                                label="Öğrenci"
                                                                color={lesson.isStudentApproved ? "success" : "default"}
                                                                variant={lesson.isStudentApproved ? "filled" : "outlined"}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                label="Öğretmen"
                                                                color={lesson.isTeacherApproved ? "success" : "default"}
                                                                variant={lesson.isTeacherApproved ? "filled" : "outlined"}
                                                            />
                                                        </Box>

                                                        {/* Approval buttons */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            {lesson.isTeacherApproved ? (
                                                                <Button
                                                                    variant="outlined"
                                                                    color="error"
                                                                    size="small"
                                                                    startIcon={<ThumbDownAltIcon />}
                                                                    onClick={() => handleWithdrawApproval(lesson.rec_no)}
                                                                    disabled={approveMutation.isPending || withdrawMutation.isPending}
                                                                >
                                                                    Onayı Geri Çek
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="contained"
                                                                    color="success"
                                                                    size="small"
                                                                    startIcon={<ThumbUpAltIcon />}
                                                                    onClick={() => handleApproveLesson(lesson.rec_no)}
                                                                    disabled={approveMutation.isPending || withdrawMutation.isPending}
                                                                >
                                                                    Onayla
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    ))}

                    {/* Summary section */}
                    {data.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 4,
                                p: 3,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                Özet Bilgiler
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                                            <SchoolIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                Toplam Ders
                                            </Typography>
                                            <Typography variant="h6">
                                                {data.length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                                            <CheckCircleIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                Tamamlanan
                                            </Typography>
                                            <Typography variant="h6">
                                                {data.filter(lesson => lesson.sonuc === '1').length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2 }}>
                                            <CalendarTodayIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                Bugünkü Dersler
                                            </Typography>
                                            <Typography variant="h6">
                                                {data.filter(lesson => isToday(new Date(lesson.derstar))).length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: theme.palette.info.main, mr: 2 }}>
                                            <ThumbUpAltIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" color="textSecondary">
                                                Onaylanan Dersler
                                            </Typography>
                                            <Typography variant="h6">
                                                {data.filter(lesson => lesson.isTeacherApproved).length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {/* Student list (only shown on larger screens) */}
                    {!isMobile && data.length > 0 && (
                        <Paper
                            elevation={3}
                            sx={{
                                mt: 4,
                                p: 3,
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                Öğrenci Listesi
                            </Typography>
                            <List sx={{ bgcolor: 'background.paper' }}>
                                {Array.from(new Set(data.map(lesson => lesson.ogrenciadi)))
                                    .filter(Boolean)
                                    .sort()
                                    .map((student, index) => {
                                        const studentLessons = data.filter(lesson => lesson.ogrenciadi === student);
                                        const subjectColor = getSubjectColor(studentLessons[0]?.ders || '');
                                        const approvedCount = studentLessons.filter(lesson => lesson.isTeacherApproved).length;

                                        return (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                    }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Badge
                                                        badgeContent={studentLessons.length}
                                                        color="primary"
                                                        overlap="circular"
                                                    >
                                                        <Avatar sx={{ bgcolor: alpha(subjectColor, 0.8) }}>
                                                            {getInitials(student)}
                                                        </Avatar>
                                                    </Badge>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {student}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {studentLessons
                                                                    .map(lesson => lesson.ders.trim())
                                                                    .filter((value, index, self) => self.indexOf(value) === index)
                                                                    .join(', ')}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={`${approvedCount} Onaylı`}
                                                                    color={approvedCount > 0 ? "success" : "default"}
                                                                    variant="outlined"
                                                                    icon={<ThumbUpAltIcon fontSize="small" />}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    }
                                                />
                                                <Chip
                                                    label={`${studentLessons.length} Ders`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha(subjectColor, 0.1),
                                                        color: subjectColor
                                                    }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                            </List>
                        </Paper>
                    )}
                </Box>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert2
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert2>
            </Snackbar>
        </Box>
    );
};

export default BirebirOgretmen;